import { useOutletContext, useParams } from "react-router-dom";
import { useState, useCallback, useMemo } from "react";
import { useMutation, useQuery } from "@apollo/client";
import MetricGrid from "@/components/MetricGrid";
import Table, { TableHeading } from "@/components/Table";
import EmptyState from "@/components/EmptyState";
import { buildInternshipColumns, type InternshipRow } from "./_utils";
import useTableRowMenu, { actionId } from "@/hooks/useTableRowMenu";
import { Student } from "../../types";
import InternshipModal, { type InternshipFormData } from "@/components/InternshipModal";
import { showSnackbar } from "@/lib/snackbarUtils";
import FacilitySideSheet from "@/features/facility/components/FacilitySideSheet";
import DeleteInternshipModal from "./_components/DeleteInternshipModal";
import { useCanManageEmployments } from "@/hooks/useCapabilities";
import { useUniversityStaffPermissions } from "@/hooks/useUniversityStaffPermissions";
import {
  CREATE_STUDENT_INTERNSHIP_MUTATION,
  UPDATE_STUDENT_INTERNSHIP_MUTATION,
  type CreateStudentInternshipInput,
  type UpdateStudentInternshipInput
} from "../../../internships/mutations";
import { STUDENT_INTERNSHIPS_QUERY } from "../../../internships/queries";
import { REQUIRED_INTERNSHIP_MONTHS } from "../../../../_shared/constants";
import InternshipHoursCalculatorDialog from "../../../internships/_components/InternshipHoursCalculator";
import { calculateMonthsBetween, isInFuture, toFormat } from "@/lib/dateHelpers";

const extractDateString = (dateValue: any): string | null => {
  if (typeof dateValue === "object" && dateValue?.startDate && dateValue.startDate !== "") {
    return dateValue.startDate;
  }
  if (typeof dateValue === "string" && dateValue !== "") {
    return dateValue;
  }
  return null;
};

export default function StudentInternshipsPage() {
  const { universityId } = useParams();
  const { student } = useOutletContext<{ student: Student }>();
  const { Menu, setMenu } = useTableRowMenu();
  const [error, setError] = useState<string | null>(null);
  const [isAddInternshipModalOpen, setIsAddInternshipModalOpen] = useState(false);
  const [isEditInternshipModalOpen, setIsEditInternshipModalOpen] = useState(false);
  const [selectedInternshipForEdit, setSelectedInternshipForEdit] = useState<any>(null);
  const [isFacilitySideSheetOpen, setIsFacilitySideSheetOpen] = useState(false);
  const [selectedFacility, setSelectedFacility] = useState<any>(null);
  const [isCalculatorOpen, setIsCalculatorOpen] = useState(false);
  const [internshipToDelete, setInternshipToDelete] = useState<InternshipRow | null>(null);
  const canDelete = useCanManageEmployments();
  const { shouldShowFacilityProfileActions } = useUniversityStaffPermissions();

  const { data, loading, refetch } = useQuery(STUDENT_INTERNSHIPS_QUERY, {
    variables: {
      input: {
        universityId: universityId?.toString() || "",
        search: student.pgaId
      }
    }
  });

  const [createStudentInternship] = useMutation(CREATE_STUDENT_INTERNSHIP_MUTATION, {
    onCompleted: (data) => {
      const response = data.createStudentInternship;
      if (response.success) {
        showSnackbar(response.message || "Internship created successfully");
        handleCloseAddModal();
        refetch();
      } else {
        setError(response.message || "Error creating internship");
      }
    },
    onError: (error) => {
      console.error("Error creating internship:", error);
      setError("Error creating internship");
    }
  });

  const [updateStudentInternship] = useMutation(UPDATE_STUDENT_INTERNSHIP_MUTATION, {
    onCompleted: (data) => {
      const response = data.updateStudentInternship;
      if (response.success) {
        showSnackbar(response.message || "Internship updated successfully");
        handleCloseEditModal();
        refetch();
      } else {
        setError(response.message || "Error updating internship");
      }
    },
    onError: (error) => {
      console.error("Error updating internship:", error);
      setError("Error updating internship");
    }
  });

  const handleOpenAddModal = useCallback(() => {
    setIsAddInternshipModalOpen(true);
  }, []);

  const handleCloseAddModal = useCallback(() => {
    setIsAddInternshipModalOpen(false);
    setError(null);
  }, []);

  const handleOpenEditModal = useCallback((internship: any) => {
    setSelectedInternshipForEdit(internship);
    setIsEditInternshipModalOpen(true);
  }, []);

  const handleCloseEditModal = useCallback(() => {
    setIsEditInternshipModalOpen(false);
    setSelectedInternshipForEdit(null);
    setError(null);
  }, []);

  const handleViewFacility = useCallback((internship: any) => {
    setSelectedFacility(internship);
    setIsFacilitySideSheetOpen(true);
  }, []);

  const handleDeleteInternship = useCallback((row: InternshipRow) => {
    setInternshipToDelete(row);
  }, []);

  const handleSubmitInternship = useCallback(
    async (data: InternshipFormData) => {
      try {
        const startDate = extractDateString(data.startDate) || data.startDate;
        const endDate = extractDateString(data.endDate);

        const graphqlInput: CreateStudentInternshipInput = {
          startedOn: startDate as string,
          endedOn: endDate,
          workingLocation: "ON_SITE"
        };

        await createStudentInternship({
          variables: {
            studentId: data.studentId || "",
            facilityId: data.facilityId,
            supervisorId: data.supervisorId,
            input: graphqlInput
          }
        });
      } catch (error) {
        console.error("Error creating internship:", error);
      }
    },
    [createStudentInternship]
  );

  const handleSubmitEditInternship = useCallback(
    async (data: InternshipFormData) => {
      if (!selectedInternshipForEdit) return;

      try {
        const startDate = extractDateString(data.startDate) || data.startDate;
        const endDate = extractDateString(data.endDate);

        const graphqlInput: UpdateStudentInternshipInput = {
          startedOn: startDate as string,
          endedOn: endDate,
          workingLocation: "ON_SITE"
        };

        await updateStudentInternship({
          variables: {
            employmentId: selectedInternshipForEdit.id,
            facilityId: data.facilityId,
            supervisorId: data.supervisorId,
            input: graphqlInput
          }
        });
      } catch (error) {
        console.error("Error updating internship:", error);
      }
    },
    [updateStudentInternship, selectedInternshipForEdit]
  );

  const internshipsData: InternshipRow[] = useMemo(() => {
    if (!data?.studentInternships?.edges) return [];

    return data.studentInternships.edges.map((edge: any) => {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to compare dates only
      const endDate = edge.node.endedOn ? new Date(edge.node.endedOn) : null;
      if (endDate) {
        endDate.setHours(0, 0, 0, 0);
      }
      const isInProgress = edge.node.isActive && edge.node.startedOn && (!endDate || endDate > today);

      // For in-progress internships, calculate months from start date to today
      // For completed internships, use the backend value
      let credits: string;
      if (isInProgress && edge.node.startedOn) {
        const calculatedMonths = calculateMonthsBetween(edge.node.startedOn, new Date());
        credits = calculatedMonths.toFixed(1);
      } else {
        credits = edge.node.internshipCredits ? edge.node.internshipCredits.toFixed(1) : "0.0";
      }

      return {
        id: edge.node.id,
        facility: edge.node.facility.name,
        supervisor: edge.node.supervisor ? `${edge.node.supervisor.firstName} ${edge.node.supervisor.lastName}` : "--",
        startDate: edge.node.startedOn,
        endDate: edge.node.endedOn,
        credits,
        status:
          edge.node.startedOn && isInFuture(edge.node.startedOn)
            ? "Not Started"
            : edge.node.isActive
              ? "In Progress"
              : "Completed",
        internship: edge.node
      };
    });
  }, [data]);

  const internshipMetrics = useMemo(() => {
    const totalCredits = internshipsData.reduce((sum, internship) => sum + parseFloat(internship.credits || "0"), 0);
    const isComplete = totalCredits >= REQUIRED_INTERNSHIP_MONTHS;

    const formatMonths = (value: number) => (value % 1 === 0 ? value.toString() : value.toFixed(1));

    return [
      {
        label: "Internship Months",
        value: `${formatMonths(totalCredits)} / ${formatMonths(REQUIRED_INTERNSHIP_MONTHS)}`,
        iconName: "business_center" as const,
        variant: isComplete ? ("success" as const) : ("warn" as const),
        loading: loading
      }
    ];
  }, [internshipsData, loading]);

  const columns = useMemo(
    () =>
      buildInternshipColumns({
        setMenu,
        actionId,
        canDelete,
        onEdit: handleOpenEditModal,
        onViewFacility: handleViewFacility,
        onDelete: handleDeleteInternship
      }),
    [setMenu, canDelete, handleOpenEditModal, handleViewFacility, handleDeleteInternship]
  );

  return (
    <>
      <MetricGrid metrics={internshipMetrics} layout="expanded" />

      <TableHeading
        heading="PGM Program Internships"
        body="View all internships associated with this student."
        actions={[
          {
            label: "Add Internship",
            icon: "add",
            variant: "filled",
            onClick: handleOpenAddModal,
            testId: "add-internship-button"
          },
          {
            label: "Internship Hours Calculator",
            icon: "calculate",
            variant: "outlined",
            onClick: () => setIsCalculatorOpen(true),
            testId: "open-internship-calculator-button"
          }
        ]}
      />
      <Table
        data={internshipsData}
        columns={columns}
        loading={loading}
        renderNoDataEmptyState={() => (
          <EmptyState
            title="No Internships"
            caption="This student does not have any internships."
            iconName="business_center"
          />
        )}
      />

      <InternshipModal
        isOpen={isAddInternshipModalOpen}
        onClose={handleCloseAddModal}
        selectedStudent={student}
        onSubmit={handleSubmitInternship}
        universityId={universityId}
        error={error}
        onClearError={() => setError(null)}
      />

      <InternshipModal
        isOpen={isEditInternshipModalOpen}
        onClose={handleCloseEditModal}
        selectedStudent={student}
        selectedInternship={selectedInternshipForEdit}
        onSubmit={handleSubmitEditInternship}
        universityId={universityId}
        error={error}
        onClearError={() => setError(null)}
      />

      {internshipToDelete && (
        <DeleteInternshipModal
          open
          onClose={() => setInternshipToDelete(null)}
          internshipId={internshipToDelete.id}
          facilityName={internshipToDelete.facility}
          startDate={internshipToDelete.startDate ? (toFormat(internshipToDelete.startDate) ?? undefined) : undefined}
          endDate={internshipToDelete.endDate ? (toFormat(internshipToDelete.endDate) ?? undefined) : undefined}
          refetchVariables={{
            input: {
              universityId: universityId?.toString() || "",
              search: student.pgaId
            }
          }}
        />
      )}

      <Menu />

      <FacilitySideSheet
        open={isFacilitySideSheetOpen && !!selectedFacility}
        onClose={() => setIsFacilitySideSheetOpen(false)}
        facilityId={selectedFacility?.internship?.facility?.id}
        customActionButtons={
          shouldShowFacilityProfileActions
            ? [
                {
                  text: "View Facility",
                  variant: "outlined" as const,
                  onClick: () => {
                    if (selectedFacility?.internship?.facility?.id) {
                      window.open(`/facilities/${selectedFacility.internship.facility.id}/details/contact`, "_blank");
                    }
                  }
                }
              ]
            : []
        }
      />

      <InternshipHoursCalculatorDialog open={isCalculatorOpen} onClose={() => setIsCalculatorOpen(false)} />
    </>
  );
}
